import { Field } from "react-hook-form";

/**
 * Function returns a boolean value, which represent whether all required fields are filled or not
 * Includes 5 steps:
 *  Step 1. @processValidationKey
 *    If validationKey is present, only this field is evaluated. It should be present, and if is an array,
 *    then all array fields should be present
 *  Step 2. @filterValidFields
 *    Filters out fields, which are not present in a form, but present in the "control._fields" variable
 *    This happens due to internal optimisations of this variable, which makes it not remove fields, which were
 *    actually removed from a form, but to shift its values down. Comparing it to "formValues" and removing
 *    invalid ones can provide a valid set of fields, which all are present in a form
 *   Step 3. @flattenFields
 *    Recursive flattening of the fields. Allows to deal with nested fields, which include cases like object array
 *    fields. E.g. additional passengers form
 *   Step 4. @filterRequiredFields
 *    Filter out fields, which are not required (field._f.required.value === false) or those which are
 *    hidden (field._f.mount === false). Also, there are fields which are functionally validated. They appear
 *    to be required, but have field._f.required.value = false. These fields should also be considered as required
 *   Step 5. @fieldsWithMissingValuesArePresent
 *    Find out if there are required fields, which doesn't have a value yet.
 *    If they're functionally validated (e.g. flight select) - execute a function
 *
 * @param fields - control._fields (contains form fields rules information)
 * @param formValues - actual form values, set by user
 * @param validationKey - allows to validation only specific field (primarily for array fields)
 * @return - boolean value, which represents if all required form fields were filled
 */

export const requiredFieldsArePresent = (
  fields: Partial<Record<string, Field>> | null,
  formValues: any,
  validationKey: string | undefined
): boolean => {
  // Step 1
  if (validationKey) return processValidationKey(validationKey, formValues);
  // Step 2
  const validFields = filterValidFields(fields!!, formValues);
  // Step 3
  const flatFields = flattenFields(validFields);
  // Step 4
  const requiredFields = filterRequiredFields(flatFields);
  // Step 5
  const requiredRulesAreViolated =
    fieldsWithMissingValuesArePresent(requiredFields);

  return !requiredRulesAreViolated;
};

const processValidationKey = (
  validationKey: string,
  formValues: any
): boolean => {
  const value = formValues[validationKey];
  if (!value) return false;

  if (Array.isArray(value)) {
    return value.every((valueField) => !!valueField);
  } else {
    return !!value;
  }
};

const filterValidFields = (
  fields: Partial<Record<string, Field>>,
  formValues: any
): Field[] => {
  let validFields: Field[] = [];
  Object.entries(fields).forEach(([key, value]) => {
    if (value?._f) {
      validFields.push(value);
    } else if (value && Array.isArray(value)) {
      const correspondingValues = formValues[key];
      if (!correspondingValues) return;

      const updatedFieldArray = value.slice(
        0,
        correspondingValues.length
      ) as unknown as Field;

      validFields.push(updatedFieldArray);
    }
  });

  return validFields;
};

const flattenFields = (fields: Field[]): Field[] => {
  let result: Field[] = [];
  fields.forEach((field: any) => {
    if (field._f) {
      result.push(field);
    } else {
      for (let key in field) {
        if (Array.isArray(field[key])) {
          result = result.concat(flattenFields(field[key] as Field[]));
        } else if (typeof field[key] === "object" && field[key] !== null) {
          result = result.concat(flattenFields([field[key] as Field]));
        }
      }
    }
  });

  return result;
};

const filterRequiredFields = (fields: Field[]): Field[] => {
  return fields.filter((field) => {
    const requiredValue = (field._f.required as any)?.value;
    const hasValidationRule = !!field._f.validate;
    const fieldIsRequired =
      hasValidationRule || requiredValue === "true" || requiredValue === true;

    return field._f.mount && fieldIsRequired;
  });
};

const fieldsWithMissingValuesArePresent = (fields: Field[]): boolean => {
  return fields.some((field) => {
    const isFunctionallyValidated = !!field._f.validate;
    let fieldValue =
      field._f.ref.hasOwnProperty("value") && isFunctionallyValidated
        ? // some fields have "undefined" when never touched and "null" when value is removed
          // for such fields it's only possible to understand that by "field._f.ref.value" field
          // applies only to functionally validated fields
          field._f.ref.value
        : field._f.value;

    if (isFunctionallyValidated && !field._f.required) {
      const validationRule = field._f.validate as Function;
      return validationRule(fieldValue) !== true;
    } else {
      return (
        fieldValue === undefined ||
        fieldValue === null ||
        fieldValue === "" ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );
    }
  });
};
